import { POS_REPORT_HIDDEN_FIELDS, POS_REPORTS, reportColumnLabel } from '@iws/backoffice-api'
import { Download, FileSpreadsheet } from 'lucide-react'
import { toast } from 'sonner'

import { useEffect, useMemo, useState } from 'react'

import { PosReportModel } from '@/http/models/PosReport'
import { useAuth } from '@/contexts/AuthContext'
import { apiErrorMessage } from '@/lib/apiError'
import { downloadCsv, downloadXlsx } from '@/lib/exportRows'

import { Button } from '@/components/ui/button'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'

import { PageHeader } from '@/components/atoms'
import { FilterSelect } from '@/components/features/pos/FilterSelect'
import { PosListCard } from '@/components/features/pos/PosListCard'
import { usePosLookups } from '@/components/features/pos/usePosLookups'
import { ReportToolbar } from '@/components/reports/ReportToolbar'
import { useReportRange } from '@/components/reports/useReportRange'

type Row = Record<string, unknown> & { _id: string }

const MAX_ROWS = 1000
const HIDDEN = new Set(POS_REPORT_HIDDEN_FIELDS)
const show = (v: unknown) =>
  v == null ? '' : typeof v === 'number' ? String(Math.round(v * 100) / 100) : typeof v === 'object' ? JSON.stringify(v) : String(v)

const PosReports = () => {
  const { can } = useAuth()
  const lookups = usePosLookups()
  const { preset, range, onPresetChange, onRangeChange } = useReportRange()
  const allowed = POS_REPORTS.filter((r) => can(r.permission))
  const [key, setKey] = useState(allowed[0]?.key ?? '')
  const [branch, setBranch] = useState('')
  const [rows, setRows] = useState<Row[]>([])
  const [count, setCount] = useState(0)
  const [loading, setLoading] = useState(false)
  const report = allowed.find((r) => r.key === key) ?? allowed[0]

  useEffect(() => {
    if (!report || range.from_date > range.to_date) return
    let cancelled = false
    setLoading(true)
    PosReportModel.getReport(report.path, { ...(report.dated ? range : {}), branch_id: branch || undefined, page: 1, length: MAX_ROWS }).then((res) => {
      if (cancelled) return
      if (res.success && res.data) {
        const list = (res.data.result ?? []).map((r, i) => ({ ...r, _id: String(r._id ?? i) }))
        setRows(list)
        setCount(Number(res.data.count ?? list.length))
      } else {
        setRows([])
        toast.error(apiErrorMessage(res, 'Could not load the report'))
      }
      setLoading(false)
    })
    return () => {
      cancelled = true
    }
  }, [report, range, branch])

  const keys = useMemo(() => [...new Set(rows.flatMap((r) => Object.keys(r)))].filter((k) => !HIDDEN.has(k)), [rows])
  const fileName = report ? `${report.key}${report.dated ? `-${range.from_date}-${range.to_date}` : ''}` : 'report'
  const header = keys.map(reportColumnLabel)

  if (!report) return <PageHeader title="POS reports" description="You have no access to any POS report." />

  return (
    <div className="space-y-4">
      <PageHeader title="POS reports" description="Sales, stock, customers and adjustments from the POS, by branch." />
      <ReportToolbar preset={preset} range={range} onPresetChange={onPresetChange} onRangeChange={onRangeChange} noRange={!report.dated} />
      <PosListCard<Row>
        rows={rows}
        loading={loading}
        empty="No figures for these dates."
        filters={
          <>
            <Select value={report.key} onValueChange={setKey}>
              <SelectTrigger aria-label="Report" className="w-full sm:w-64">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {allowed.map((r) => (
                  <SelectItem key={r.key} value={r.key}>{r.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <FilterSelect label="Branch" value={branch} onChange={setBranch} allLabel="All branches" options={lookups.branches.map((b) => ({ label: b.name, value: b._id }))} />
            <div className="flex gap-2 sm:ml-auto">
              <Button variant="outline" disabled={!rows.length} onClick={() => downloadCsv(fileName, header, rows.map((r) => keys.map((k) => show(r[k]))))}>
                <Download /> CSV
              </Button>
              <Button
                variant="outline"
                disabled={!rows.length}
                onClick={() =>
                  downloadXlsx(fileName, header, rows.map((r) => keys.map((k) => (typeof r[k] === 'number' ? (r[k] as number) : show(r[k])))), report.label).catch(() =>
                    toast.error('Could not make the Excel file')
                  )
                }
              >
                <FileSpreadsheet /> Excel
              </Button>
            </div>
          </>
        }
        columns={keys.map((k) => ({
          label: reportColumnLabel(k),
          className: typeof rows[0]?.[k] === 'number' ? 'text-right tabular-nums' : undefined,
          render: (r: Row) => show(r[k]),
        }))}
      />
      {count > rows.length && <p className="text-sm text-muted-foreground">Showing the first {rows.length} of {count} rows. Choose fewer days or one branch for the rest.</p>}
    </div>
  )
}

export default PosReports
